import { SupportModalService } from './../support-modal-service/support-modal.service';
import { ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AnalyticsEventProperties, AnalyticsEvents, MixpanelService } from '@app/services/mixpanel.service';

// material
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SupportSnackBarComponent } from '../snack-bar/support-snack-bar.component';

// module
import { Product, SeverityOptions, SupportFormPayload } from '../model';

// FF
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { FeatureFlag } from '@app/infrastructure/feature-flag-manager/feature-flag-decorator';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { UnsubscribeService } from '@app/infrastructure/unsubscribe/unsubscribe.service';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-support-modal',
	templateUrl: './support-modal.component.html',
	styleUrls: ['./support-modal.component.scss'],
	providers: [SupportModalService, UnsubscribeService],
	encapsulation: ViewEncapsulation.None,
})
export class SupportModalComponent implements OnInit {
	@FeatureFlag(FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB) enableOptimizationHub: boolean;

	public productItems: Product[];
	public modalDescription: string | undefined;
	public severityOptions = Object.values(SeverityOptions);
	public supportForm: FormGroup;
	public isLoading: boolean = false;
	constructor(
		private unsubscribe$: UnsubscribeService,
		private cdr: ChangeDetectorRef,
		private dialogRef: MatDialogRef<SupportModalComponent>,
		private fb: FormBuilder,
		private _snackBar: MatSnackBar,
		private supportModalService: SupportModalService,
		public featureFlagManagerService: FeatureFlagManagerService,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			userName: string;
			userEmail: string;
			orgId: string;
			payload: { product: Product; message?: string };
		},
		private mixpanelService: MixpanelService,
	) {}
	public ngOnInit(): void {
		this.productItems = Object.values(Product);
		if (!this.enableOptimizationHub) {
			const optHubDependantProducts = [Product.insights, Product.kompass];

			this.productItems = this.productItems.filter((product) => !optHubDependantProducts.includes(product));
		} else {
			this.productItems = this.productItems.map((product) => {
				if (product === Product.computeExplorer) {
					return 'Commitment Manager' as Product;
				}
				return product;
			});
		}

		this.modalDescription = this.data.payload?.message;

		this.supportForm = this.fb.group({
			productName: [
				{
					value: this.data.payload?.product ? this.data.payload?.product : null,
					disabled: !!this.data.payload?.product,
				},
				[Validators.required],
			],
			subject: [
				this.data.payload ? `I’d like to talk about ${this.data.payload?.product}` : '',
				[Validators.required, Validators.maxLength(1000)],
			],
			description: [
				this.data.payload ? `Hi,\nPlease contact me about ${this.data.payload?.product}.` : '',
				[Validators.required, Validators.maxLength(1000)],
			],
		});

		this.supportModalService
			.getActiveProducts()
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((response) => {
				if (response.product_permissions.Kompass || response.product_permissions.zesty_disk) {
					const newControl = new FormControl('', Validators.required);
					this.supportForm.addControl('severity', newControl);
					this.cdr.detectChanges();
				}
			});
	}

	public close(): void {
		this.dialogRef.close();
	}

	public submit(): void {
		this.isLoading = true;
		this.supportForm.disable();
		const requestBody: SupportFormPayload = {
			...this.data,
			...this.supportForm.value,
		};
		this.supportModalService.submitCase(requestBody).subscribe(
			(response: { success: boolean; id: string; errors: Error[] }) => {
				if (response.success) {
					this.dialogRef.close(true);
				} else {
					this.openSnackBar();
				}
			},
			(error: Error) => {
				this.isLoading = false;
				this.openSnackBar();
			},
		);
		if (this.enableOptimizationHub) {
			this.mixpanelService.track(AnalyticsEvents.click, {
				[AnalyticsEventProperties.componentName]: 'Contact us form',
				[AnalyticsEventProperties.actionName]: 'Send',
			});
		}
	}

	private openSnackBar(): void {
		const snackBarRef = this._snackBar.openFromComponent(SupportSnackBarComponent, {
			verticalPosition: 'top',
			panelClass: ['snack-style'],
			data: {
				preClose: () => {
					snackBarRef.dismiss();
				},
			},
		});
	}
}
